import { Flex, FlexProps } from '@chakra-ui/react'
import React from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { LeadText, SidebarFilterCounter } from '~shared-components/components'
import { toggleSidebarFiltersAction } from '~shared-components/filters/filter.actions'
import { FilterEndpoint } from '~shared-components/filters/filter.reducers'
import { FilterSelector } from '~shared-components/filters/filter.selector'
import { useTranslations } from '~shared-components/hooks/useTranslations'
import { LocationSelector } from '~shared-components/redux/selectors/location.selector'

import { SidebarToggleButton } from './SidebarToggleButton'

interface SidebarFilterToggleProps {
  isOpen?: boolean
  omitFilters?: string[]
  onClick?: () => void
}

const toggleMs = '250ms'
const toggleAni = (props: string, delay = '') =>
  `${props} ${toggleMs} ease-in-out ${delay}`

const cssAnimatedToggleWrapperFn = ({
  isOpen,
}: AnimatedToggleWrapperProps) => ({
  '& > .filter-counter': {
    transform: `rotate(${isOpen ? '0' : '90'}deg) translateX(${
      isOpen ? '0' : '3'
    }px);`,
    transition: toggleAni('transform', isOpen ? toggleMs : undefined),
  },
  '& > button': {
    transform: `rotate(${isOpen ? '0' : '90'}deg)`,
    transition: toggleAni('transform', isOpen ? toggleMs : undefined),
  },
  _left: isOpen ? '0px' : '25px',
  _top: isOpen ? '0px' : '30px',
  minWidth: isOpen ? '100%' : '0%',
  transform: isOpen
    ? 'rotate(0deg) translate(0px, 0px)'
    : 'rotate(-90deg) translate(68px,-20px)',
  transformOrigin: 'right bottom',
  transition: isOpen
    ? [toggleAni('transform'), toggleAni('min-width', toggleMs)].join(',')
    : [toggleAni('min-width'), toggleAni('transform', toggleMs)].join(','),
})
interface AnimatedToggleWrapperProps extends FlexProps {
  isOpen?: boolean
}
const AnimatedToggleWrapper: React.FC<AnimatedToggleWrapperProps> = props => (
  <Flex
    alignSelf="flex-end"
    css={cssAnimatedToggleWrapperFn}
    height="auto"
    justifyContent="space-between"
    margin="4px 0 12px 0"
    pb={[1, 2]}
    position="relative"
    pt={[1, '48px']}
    px={[1, 5]}
    whiteSpace="nowrap"
    zIndex={10}
    {...props}
  />
)

export const SidebarFilterToggle = ({
  isOpen,
  omitFilters,
  onClick,
}: SidebarFilterToggleProps) => {
  const t = useTranslations()
  const pageType = useSelector(LocationSelector.pageType)
  const numberActiveFilters: number =
    useSelector(
      FilterSelector.numberActiveFiltersByEndpoint(
        pageType as FilterEndpoint,
        omitFilters,
      ),
    ) || 0

  const dispatch = useDispatch()

  const toggleSidebar = () => {
    if (onClick) {
      dispatch(toggleSidebarFiltersAction())
      onClick()
    }
  }

  return (
    <AnimatedToggleWrapper isOpen={isOpen}>
      <Flex alignItems="baseline" flexWrap="nowrap" mr={3}>
        <LeadText bold color="neutral.90" mr={1} mt="2px" whiteSpace="nowrap">
          {t('filter.filterby')}
        </LeadText>
        {Boolean(numberActiveFilters) && (
          <SidebarFilterCounter
            className="filter-counter"
            height="16px"
            label={numberActiveFilters.toString()}
          />
        )}
      </Flex>
      <SidebarToggleButton isOpen={isOpen} onClick={toggleSidebar} />
    </AnimatedToggleWrapper>
  )
}
